import { useEffect, useMemo, useState } from 'react';
import { CalendarOff, Loader2, Save } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { studioGetAvailability, studioUpdateAvailability, apiErrorMessage } from '@/lib/api';
import { parseIsoDate, formatIsoDate } from '@/lib/dateUtils';
import { ADMIN_MY_PROFILE as T } from '@/constants/testIds';

const today = new Date();
today.setHours(0, 0, 0, 0);

/** Photographer's own booking calendar: click a date to block it off; dates
 * with an active session are locked (derived from bookings, not editable here). */
export const AdminAvailabilityCard = () => {
  const [data, setData] = useState(null); // { blocked_dates, booked_dates }
  const [blocked, setBlocked] = useState([]); // Date[]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    studioGetAvailability()
      .then((d) => { setData(d); setBlocked((d.blocked_dates || []).map(parseIsoDate).filter(Boolean)); })
      .catch(() => toast.error('Could not load your availability.'));
  }, []);

  const bookedDates = useMemo(() => (data?.booked_dates || []).map((b) => parseIsoDate(b.date)).filter(Boolean), [data]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await studioUpdateAvailability(blocked.map(formatIsoDate));
      setData(updated);
      setBlocked((updated.blocked_dates || []).map(parseIsoDate).filter(Boolean));
      toast.success('Availability updated.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save availability.'));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Skeleton className="h-96 rounded-2xl bg-[#141416]" />;

  return (
    <section data-testid={T.availabilityCard} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl tracking-[0.06em] text-[hsl(var(--lxra-fg))]">AVAILABILITY</h3>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Click a date to block it off. Booked sessions lock automatically and show on your public profile.</p>
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid={T.availabilitySave} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save availability</>}
        </Button>
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <Calendar
          mode="multiple"
          numberOfMonths={2}
          selected={blocked}
          onSelect={(days) => setBlocked(days || [])}
          modifiers={{ booked: bookedDates }}
          modifiersClassNames={{ booked: 'bg-red-500/25 text-red-200 rounded-md pointer-events-none' }}
          disabled={[...bookedDates, { before: today }]}
          fromMonth={today}
          data-testid={T.availabilityCalendar}
          classNames={{ day_selected: 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] focus:bg-[hsl(var(--lxra-gold))]' }}
          className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416]"
        />
        <div className="flex-1 min-w-[180px] space-y-3 text-xs text-[hsl(var(--lxra-muted))]">
          <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lxra-gold))]" /> Blocked by you</p>
          <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-400" /> Booked session</p>
          {data.booked_dates?.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {data.booked_dates.slice(0, 8).map((b) => <li key={b.booking_id} className="text-[hsl(var(--lxra-fg))]">{b.date} <span className="text-[hsl(var(--lxra-muted))]">— {b.title}</span></li>)}
            </ul>
          ) : (
            <p className="flex items-center gap-1.5 mt-3"><CalendarOff className="h-3.5 w-3.5" /> No booked sessions yet.</p>
          )}
        </div>
      </div>
    </section>
  );
};
